/**
 * Small shared helpers for the Meetings screens: how times are written, and
 * what an RSVP is called.
 */

export const RSVP_LABELS = {
    pending: 'Awaiting reply',
    accepted: 'Accepted',
    declined: 'Declined',
    tentative: 'Maybe'
};

export const timeText = (date) => new Date(date)
    .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

export const dayText = (date, options = { weekday: 'long', day: 'numeric', month: 'long' }) => new Date(date)
    .toLocaleDateString([], options);

const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();

/** "10:00 AM – 11:00 AM", "All day", or a date range when it spans days. */
export const rangeText = (meeting) => {
    const { startsAt, endsAt, allDay } = meeting;
    if (allDay) {
        return sameDay(startsAt, endsAt)
            ? 'All day'
            : `${dayText(startsAt, { day: 'numeric', month: 'short' })} – ${dayText(endsAt, { day: 'numeric', month: 'short' })}`;
    }
    if (sameDay(startsAt, endsAt)) return `${timeText(startsAt)} – ${timeText(endsAt)}`;
    return `${dayText(startsAt, { day: 'numeric', month: 'short' })} ${timeText(startsAt)} – `
        + `${dayText(endsAt, { day: 'numeric', month: 'short' })} ${timeText(endsAt)}`;
};

const pad = (n) => String(n).padStart(2, '0');

/** A Date as the value a <input type="datetime-local"> (or "date") expects, in local time. */
export const toInputValue = (date, allDay) => {
    const d = new Date(date);
    const day = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return allDay ? day : `${day}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** The reverse: an input's local value to an ISO instant. All-day runs midnight to end of day. */
export const fromInputValue = (value, allDay, { endOfDay = false } = {}) => {
    if (!value) return null;
    const d = allDay
        ? new Date(`${value}T${endOfDay ? '23:59:59' : '00:00:00'}`)
        : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

export const browserTimezone = () => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata'; } catch { return 'Asia/Kolkata'; }
};

export const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mirrors server/utils/meetingAccess.js: the room opens 15 minutes before the
// start and stays open for two hours after the end. The server enforces it;
// this only decides whether to show the Join button.
const OPENS_MIN_BEFORE = 15;
const CLOSES_MIN_AFTER = 120;

export const joinState = (meeting, now = new Date()) => {
    if (!meeting?.hasRoom || !['scheduled', 'live'].includes(meeting.status)) return { can: false };
    const opensAt = new Date(new Date(meeting.startsAt).getTime() - OPENS_MIN_BEFORE * 60000);
    const closesAt = new Date(new Date(meeting.endsAt).getTime() + CLOSES_MIN_AFTER * 60000);
    if (now < opensAt) return { can: false, opensAt };
    if (now > closesAt) return { can: false };
    return { can: true };
};

const LIST_DAYS = 180;
const DAY_MS = 86400000;

/**
 * Past or upcoming? By the clock, not the calendar day: a meeting that finished
 * at 2 pm is past at 2:01 pm, not tomorrow. One a host ended early is past too.
 * One still running (it ends later) is not past - it is the one to go back to.
 */
export const isPast = (meeting, now = new Date()) => meeting.status === 'ended' || new Date(meeting.endsAt) <= now;

/** The dates to ask the server for, for the Upcoming or the Past list. */
export const listWindow = (tab, now = new Date()) => (tab === 'past'
    ? { from: new Date(now.getTime() - LIST_DAYS * DAY_MS), to: now }
    : { from: now, to: new Date(now.getTime() + LIST_DAYS * DAY_MS) });

/** Whether a meeting from that window belongs on this tab. */
export const belongsToTab = (meeting, tab, now = new Date()) => (tab === 'past' ? isPast(meeting, now) : !isPast(meeting, now));
